// ============================================================
// The kind and the rate of fire an attack line's beat carries (plan design-gaps, K1): read ONCE off the attack
// context, by the one reading in engine/attackBeat, so the three writers (the landed entry, the miss, the jam)
// say the same thing about the same attack.
// ============================================================
import {
  attackKindOf,
  rateOfFireOf,
  type AreaBurstInput,
  type AttackKind,
  type RateOfFire,
} from '../../engine/attackBeat';
import { overwatchOf } from '../../engine/overwatch';
import { actingCombatantId } from '../../engine/turnTracker';
import type { HitResult } from '../../engine/hitDamageTypes';
import type { AttackContext } from './context';

export interface BeatKindFacts {
  kind: AttackKind;
  rof: RateOfFire | null;
  /** The blast template one victim was caught in (T2: AttackVictim `area`), set per victim. */
  area?: AreaBurstInput;
}

/** What the context knows of the action and the rate: the toggles, suppression, and the effective fire mode. */
export function beatKindFacts(ctx: AttackContext, melee: boolean, fireMode: string): BeatKindFacts {
  const facts = {
    melee,
    fireMode,
    charge: ctx.isCharge,
    allOut: ctx.isAllOutAttack,
    guarded: ctx.isGuardedAttack,
    suppressing: ctx.suppressing,
    ram: !!ctx.ramming,
    overwatch: interrupts(ctx),
  };
  return { kind: attackKindOf(facts), rof: rateOfFireOf(facts) };
}

/**
 * An Overwatch interrupt (K5, the Track K review): the actor holds a zone and fires OUT OF THEIR OWN TURN - the
 * order running and somebody else acting. A shot on their own turn, or with no order, is an ordinary one.
 */
function interrupts(ctx: AttackContext): boolean {
  if (!overwatchOf(ctx.actorConditionContext?.conditions)) return false;
  const acting = actingCombatantId(ctx.turnTrackerState ?? null);
  return !!acting && acting !== ctx.actorId;
}

/** The cover the hits met: the most AP any one of them paid, 0 in the open. */
export const coverApOf = (hits: readonly HitResult[]): number => Math.max(0, ...hits.map((h) => h.coverAP || 0));
